import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageShell, { SectionTitle } from './PageShell';
import { COMPETENCES } from './LivretPage';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { QRCodeSVG } from 'qrcode.react';
import { Users, Copy, CheckCircle2, ArrowLeft, Loader2, Mail, Lock, Link as LinkIcon, X, Star, QrCode, MessageCircle, Send, Maximize2, RefreshCw, Sparkles, Car, ArrowRight, Presentation, ClipboardList, Search } from 'lucide-react';
import FlashSeanceModal from '../components/FlashSeanceModal';
import TeacherActuPanel from '../components/TeacherActuPanel';
import { getPublicOrigin } from '../lib/publicUrl';
import { RecentLessonsSection, StudentBilansSection } from '../components/StudentBilansSection';

const FORMULA_BADGE = {
  permis_b: ['Permis B', 'bg-sky-500/15 border-sky-500/40 text-sky-300'],
  boite_auto: ['Boîte auto', 'bg-violet-500/15 border-violet-500/40 text-violet-300'],
  aac: ['AAC', 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300'],
  cs: ['CS', 'bg-teal-500/15 border-teal-500/40 text-teal-300'],
  am: ['AM', 'bg-amber-500/15 border-amber-500/40 text-amber-300'],
  a1: ['A1', 'bg-orange-500/15 border-orange-500/40 text-orange-300'],
  a2: ['A2', 'bg-red-500/15 border-red-500/40 text-red-300'],
  passerelle: ['Passerelle A2→A', 'bg-pink-500/15 border-pink-500/40 text-pink-300'],
  be: ['BE', 'bg-indigo-500/15 border-indigo-500/40 text-indigo-300'],
  b96: ['B96', 'bg-cyan-500/15 border-cyan-500/40 text-cyan-300'],
};

export const FormulaBadge = ({ formula }) => {
  if (!formula || !FORMULA_BADGE[formula]) return <span className="text-white/30 text-[11px]">—</span>;
  const [label, cls] = FORMULA_BADGE[formula];
  return <span className={`px-2 py-0.5 rounded-full border text-[10.5px] font-bold whitespace-nowrap ${cls}`} data-testid={`formula-badge-${formula}`}>{label}</span>;
};

export const HoursCell = ({ done, pack }) => {
  if (done == null || !pack) return <span className="text-white/30 text-[12px]">—</span>;
  const remaining = Math.round((pack - done) * 10) / 10;
  const pct = Math.min(100, Math.round((done / pack) * 100));
  const over = remaining < 0;
  const low = !over && remaining <= 2;
  return (
    <div className="min-w-[130px]">
      <div className="text-white/80 text-[12.5px] font-semibold">{done} / {pack} h</div>
      <div className="h-1.5 max-w-[120px] bg-white/8 rounded-full overflow-hidden my-1">
        <div className={`h-full ${over ? 'bg-red-500' : low ? 'bg-orange-400' : pct >= 75 ? 'bg-yellow-400' : 'bg-emerald-500'}`} style={{ width: `${pct}%` }} />
      </div>
      {over
        ? <div className="text-red-400 text-[11px] font-bold">⚠ Forfait dépassé de {Math.abs(remaining)} h — heures supp. à prévoir</div>
        : <div className={`text-[11px] font-semibold ${low ? 'text-orange-300' : 'text-white/45'}`}>{low ? '⚠ ' : ''}restant : {remaining} h</div>}
    </div>
  );
};

export function TeacherDashboardPage() {
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [showFlashModal, setShowFlashModal] = useState(false);
  const [formulaFilter, setFormulaFilter] = useState('');
  const [studentSearch, setStudentSearch] = useState('');

  const fetchStudents = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const d = await api.teacherStudents();
      setData(d || { teacher_code: null, students: [] });
      setLastRefresh(new Date());
    } catch (e) {
      console.error('teacherStudents error', e);
      if (!silent) setData({ teacher_code: null, students: [] });
    } finally {
      if (!silent) setRefreshing(false);
      setLoading(false);
    }
  }, []);

  // Fetch initial + rafraîchissement auto quand un élève scanne le QR code
  useEffect(() => {
    if (!user || user.role !== 'enseignant') { setLoading(false); return undefined; }
    fetchStudents();
    // Polling toutes les 15 s pour capter les rattachements en direct
    const interval = setInterval(() => fetchStudents(true), 15000);
    // Rafraîchit aussi quand la fenêtre reprend le focus (retour d'onglet)
    const onFocus = () => fetchStudents(true);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') fetchStudents(true);
    });
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [user, fetchStudents]);

  const copyCode = () => {
    if (!data?.teacher_code) return;
    navigator.clipboard.writeText(data.teacher_code);
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };

  const joinUrl = data?.teacher_code ? `${getPublicOrigin()}/rejoindre?code=${data.teacher_code}` : '';
  const shareMsg = () => `Salut ! Voici le lien pour te rattacher à moi sur codevoiture.fr : ${joinUrl}\n(ou code : ${data?.teacher_code})\n${user?.name || ''}`;
  const shareSMS = () => { window.location.href = `sms:?body=${encodeURIComponent(shareMsg())}`; };
  const shareWA = () => { window.open(`https://wa.me/?text=${encodeURIComponent(shareMsg())}`, '_blank'); };
  const copyLink = () => { navigator.clipboard.writeText(joinUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  if (authLoading) return <PageShell><div className="max-w-7xl mx-auto p-12 text-white/50">Chargement…</div></PageShell>;
  if (!user) return <PageShell><div className="max-w-3xl mx-auto p-12 text-center"><Lock className="mx-auto text-red-400 mb-4" size={32} /><h2 className="font-serif-editorial text-white text-[28px]">Espace enseignant</h2><p className="text-white/55 mt-2">Connecte-toi avec un compte enseignant.</p></div></PageShell>;
  if (user.role !== 'enseignant') return <PageShell><div className="max-w-3xl mx-auto p-12 text-center"><Lock className="mx-auto text-red-400 mb-4" size={32} /><h2 className="font-serif-editorial text-white text-[28px]">Réservé aux enseignants ECSR</h2><p className="text-white/55 mt-2">Ton compte est en rôle <strong className="text-white">{user.role}</strong>. Crée un compte enseignant pour accéder au dashboard.</p></div></PageShell>;

  return (
    <PageShell minimal>
      <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-8 sm:mt-10">
        <SectionTitle kicker={`Bonjour ${user.name} · Enseignant ECSR`} title="Mon tableau de bord" sub="Vue d’ensemble de tes élèves rattachés, leur progression livret et leur lecture magazine." />

        {/* Teacher code card */}
        <div className="rounded-xl sm:rounded-2xl border border-red-900/30 bg-gradient-to-br from-red-950/30 to-[#0d0d0e] p-5 sm:p-6 lg:p-8 mb-6 sm:mb-8">
          <div className="grid md:grid-cols-[1fr_auto] gap-6 items-start">
            <div>
              <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-red-400 font-bold mb-2 inline-flex items-center gap-1.5"><LinkIcon size={12} /> Code de rattachement · à partager</div>
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                <span className="font-serif-editorial text-white text-[32px] sm:text-[44px] lg:text-[60px] leading-none tracking-wider font-mono-spec" data-testid="teacher-code-display">{data?.teacher_code || '—'}</span>
                <button onClick={copyCode} className="h-9 sm:h-10 px-3 rounded-full border border-white/15 text-white/80 hover:border-white/40 text-[12.5px] sm:text-[13px] font-medium inline-flex items-center gap-2">{copied ? <><CheckCircle2 size={14} className="text-emerald-400" /> Copié</> : <><Copy size={14} /> Copier code</>}</button>
              </div>
              <p className="text-white/55 text-[12.5px] sm:text-[13.5px] mt-3 max-w-xl">3 façons de rattacher un élève : <strong className="text-white">1)</strong> il scanne ton QR code → 2) tu lui envoies le lien magique par SMS/WhatsApp → 3) il saisit le code manuellement dans son espace.</p>
              <div className="mt-3 sm:mt-4 flex flex-wrap gap-2">
                <button onClick={() => setShowQrModal(true)} data-testid="teacher-show-qr" className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] font-bold shadow-[0_0_20px_-4px_rgba(239,68,68,0.6)]">
                  <QrCode size={14} /> Afficher QR code en grand
                </button>
                <button onClick={shareSMS} className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-red-500/40 bg-red-500/10 text-red-200 hover:bg-red-500/20 text-[12.5px] font-semibold">
                  <MessageCircle size={13} /> SMS
                </button>
                <button onClick={shareWA} className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 text-[12.5px] font-semibold">
                  <Send size={13} /> WhatsApp
                </button>
                <button onClick={copyLink} className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-white/15 text-white/80 hover:border-white/40 text-[12.5px] font-semibold">
                  <LinkIcon size={13} /> Copier lien
                </button>
              </div>
            </div>
            <div className="flex flex-col items-center gap-2 shrink-0">
              {data?.teacher_code && (
                <button onClick={() => setShowQrModal(true)} className="group relative bg-white p-3 rounded-xl hover:scale-105 transition-transform" title="Cliquer pour agrandir" data-testid="teacher-qr-small">
                  <QRCodeSVG value={joinUrl} size={130} level="M" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-black/40 rounded-xl transition"><Maximize2 size={22} className="text-white" /></div>
                </button>
              )}
              <div className="text-white/45 text-[10.5px] font-mono-spec text-center">L&apos;élève scanne · rattachement instantané</div>
              <div className="text-red-300 text-[11px] font-mono-spec font-bold text-center">Code : {data?.teacher_code || '—'}</div>
              <div className="font-serif-editorial text-red-500 text-[28px] leading-none mt-1">{data?.students?.length || 0}</div>
              <div className="text-white/40 text-[10px] font-mono-spec uppercase tracking-wider">Élève{(data?.students?.length || 0) > 1 ? 's' : ''} rattaché{(data?.students?.length || 0) > 1 ? 's' : ''}</div>
            </div>
          </div>
        </div>

        {/* ═══ 2. Outils pédagogiques — regroupés par usage ═══ */}
        <div className="mb-6 sm:mb-8" data-testid="teacher-tools-section">
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/45 font-bold font-mono-spec mb-3">Mes outils pédagogiques</div>
          <div className="grid md:grid-cols-3 gap-3 sm:gap-4">
            {/* En voiture */}
            <div className="rounded-2xl border border-emerald-700/30 bg-gradient-to-br from-emerald-950/20 to-[#0d0d0e] p-4 sm:p-5" data-testid="teacher-tools-voiture">
              <div className="flex items-center gap-2.5 mb-3">
                <span className="w-9 h-9 rounded-lg bg-emerald-600/15 border border-emerald-700/40 flex items-center justify-center text-emerald-400"><Car size={16} /></span>
                <div>
                  <div className="text-emerald-300 text-[12px] font-bold font-mono-spec uppercase tracking-wider">En voiture</div>
                  <div className="text-white/40 text-[10.5px]">Pendant la leçon, sur ton téléphone</div>
                </div>
              </div>
              <div className="space-y-1.5">
                <Link to="/enseignant/examen-blanc-grille" data-testid="tool-bilans" className="flex items-center justify-between gap-2 rounded-lg px-3 py-2.5 bg-white/[0.04] hover:bg-emerald-600/15 text-white/85 hover:text-white text-[13px] font-semibold transition">
                  Bilans & permis blanc <ArrowRight size={13} className="text-emerald-400 shrink-0" />
                </Link>
                <Link to="/enseignant/cours-en-voiture" data-testid="tool-top10" className="flex items-center justify-between gap-2 rounded-lg px-3 py-2.5 bg-white/[0.04] hover:bg-emerald-600/15 text-white/85 hover:text-white text-[13px] font-semibold transition">
                  Top 10 pré-requis · visuels <ArrowRight size={13} className="text-emerald-400 shrink-0" />
                </Link>
                <button onClick={() => setShowFlashModal(true)} data-testid="teacher-flash-seance-btn" className="w-full flex items-center justify-between gap-2 rounded-lg px-3 py-2.5 bg-white/[0.04] hover:bg-emerald-600/15 text-white/85 hover:text-white text-[13px] font-semibold transition text-left">
                  <span className="inline-flex items-center gap-1.5"><Sparkles size={12} className="text-emerald-400" /> Fiche flash · QR fin de séance</span>
                  <ArrowRight size={13} className="text-emerald-400 shrink-0" />
                </button>
              </div>
            </div>
            {/* Rendez-vous & salle */}
            <div className="rounded-2xl border border-sky-700/30 bg-gradient-to-br from-sky-950/20 to-[#0d0d0e] p-4 sm:p-5" data-testid="teacher-tools-rdv">
              <div className="flex items-center gap-2.5 mb-3">
                <span className="w-9 h-9 rounded-lg bg-sky-600/15 border border-sky-700/40 flex items-center justify-center text-sky-400"><Presentation size={16} /></span>
                <div>
                  <div className="text-sky-300 text-[12px] font-bold font-mono-spec uppercase tracking-wider">Rendez-vous & salle</div>
                  <div className="text-white/40 text-[10.5px]">Présentations et animations de groupe</div>
                </div>
              </div>
              <div className="space-y-1.5">
                <Link to="/enseignant/rendez-vous-pedagogiques" data-testid="teacher-go-rvp" className="flex items-center justify-between gap-2 rounded-lg px-3 py-2.5 bg-white/[0.04] hover:bg-sky-600/15 text-white/85 hover:text-white text-[13px] font-semibold transition">
                  RVP 1 · 2 · 3 + RDV préalable (AAC) <ArrowRight size={13} className="text-sky-400 shrink-0" />
                </Link>
                <Link to="/enseignant/seances" data-testid="teacher-go-seances" className="flex items-center justify-between gap-2 rounded-lg px-3 py-2.5 bg-white/[0.04] hover:bg-sky-600/15 text-white/85 hover:text-white text-[13px] font-semibold transition">
                  Mes séances pédagogiques <ArrowRight size={13} className="text-sky-400 shrink-0" />
                </Link>
                <Link to="/enseignant/cours-en-salle" data-testid="tool-salle" className="flex items-center justify-between gap-2 rounded-lg px-3 py-2.5 bg-white/[0.04] hover:bg-sky-600/15 text-white/85 hover:text-white text-[13px] font-semibold transition">
                  Cours en salle <ArrowRight size={13} className="text-sky-400 shrink-0" />
                </Link>
              </div>
            </div>
            {/* Suivi & fiches */}
            <div className="rounded-2xl border border-amber-700/30 bg-gradient-to-br from-amber-950/15 to-[#0d0d0e] p-4 sm:p-5" data-testid="teacher-tools-suivi">
              <div className="flex items-center gap-2.5 mb-3">
                <span className="w-9 h-9 rounded-lg bg-amber-600/15 border border-amber-700/40 flex items-center justify-center text-amber-400"><ClipboardList size={16} /></span>
                <div>
                  <div className="text-amber-300 text-[12px] font-bold font-mono-spec uppercase tracking-wider">Suivi & fiches</div>
                  <div className="text-white/40 text-[10.5px]">Trames, comptes-rendus et archives</div>
                </div>
              </div>
              <div className="space-y-1.5">
                <Link to="/cours/progression" data-testid="tool-trame" className="flex items-center justify-between gap-2 rounded-lg px-3 py-2.5 bg-white/[0.04] hover:bg-amber-600/15 text-white/85 hover:text-white text-[13px] font-semibold transition">
                  Ma trame de cours (23 étapes) <ArrowRight size={13} className="text-amber-400 shrink-0" />
                </Link>
                <Link to="/enseignant/fiches" data-testid="teacher-go-fiches" className="flex items-center justify-between gap-2 rounded-lg px-3 py-2.5 bg-white/[0.04] hover:bg-amber-600/15 text-white/85 hover:text-white text-[13px] font-semibold transition">
                  Fiches de séance <ArrowRight size={13} className="text-amber-400 shrink-0" />
                </Link>
                <Link to="/enseignant/mes-cours" data-testid="tool-mescours" className="flex items-center justify-between gap-2 rounded-lg px-3 py-2.5 bg-white/[0.04] hover:bg-amber-600/15 text-white/85 hover:text-white text-[13px] font-semibold transition">
                  Tous mes cours · hub complet <ArrowRight size={13} className="text-amber-400 shrink-0" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Flash séance modal */}
        <FlashSeanceModal
          open={showFlashModal}
          onClose={() => setShowFlashModal(false)}
          onCreated={() => { /* future : refresh liste des fiches */ }}
        />

        {/* QR Code fullscreen modal */}
        {showQrModal && data?.teacher_code && (
          <div className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center p-4" onClick={() => setShowQrModal(false)} data-testid="teacher-qr-modal">
            <div className="max-w-md w-full rounded-2xl border border-red-500/30 bg-[#0d0d0e] p-8 text-center" onClick={(e) => e.stopPropagation()}>
              <div className="text-red-400 text-[11px] uppercase tracking-[0.22em] font-mono-spec font-bold">Rattachement instantané</div>
              <h2 className="mt-2 font-serif-editorial text-white text-[26px] leading-tight">L&apos;élève scanne, on est liés</h2>
              <div className="mt-6 flex justify-center">
                <div className="bg-white p-5 rounded-2xl">
                  <QRCodeSVG value={joinUrl} size={280} level="H" />
                </div>
              </div>
              <div className="mt-5">
                <div className="text-white/50 text-[11px] font-mono-spec uppercase tracking-wider">Ou code manuel</div>
                <div className="mt-1 font-serif-editorial text-red-400 text-[36px] font-mono-spec tracking-wider font-bold">{data.teacher_code}</div>
              </div>
              <p className="mt-4 text-white/60 text-[12.5px] leading-relaxed">L&apos;élève ouvre l&apos;appareil photo de son téléphone, cadre le QR code, appuie sur la notification → il arrive directement sur codevoiture.fr avec ton code pré-rempli. Il crée un compte gratuit en 30 s et le rattachement est automatique.</p>
              <button onClick={() => setShowQrModal(false)} className="mt-6 h-11 w-full rounded-full bg-white/10 hover:bg-white/15 text-white text-[13px] font-semibold">Fermer</button>
            </div>
          </div>
        )}

        {/* ═══ 3. Mes élèves ═══ */}
        <div id="mes-eleves" className="mb-6 sm:mb-8 scroll-mt-20" data-testid="teacher-students-section">
          <div className="flex items-center gap-3 flex-wrap mb-3">
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/45 font-bold font-mono-spec inline-flex items-center gap-1.5">
              <Users size={12} /> Mes élèves · {data?.students?.length || 0}
            </div>
            <button
              onClick={() => fetchStudents(false)}
              disabled={refreshing}
              data-testid="teacher-refresh-students"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-emerald-500/40 bg-emerald-600/10 text-emerald-300 hover:bg-emerald-600/20 disabled:opacity-50 text-[11.5px] font-bold transition"
            >
              <RefreshCw size={11} className={refreshing ? 'animate-spin' : ''} /> {refreshing ? 'Actualisation…' : 'Actualiser'}
            </button>
            {lastRefresh && (
              <span className="text-white/35 text-[10.5px] font-mono-spec">
                MàJ {lastRefresh.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} · auto 15 s
              </span>
            )}
          </div>
          {loading ? <div className="text-white/50 inline-flex items-center gap-2 py-10"><Loader2 size={16} className="animate-spin" /> Chargement…</div> : (!data?.students || data.students.length === 0) ? (
          <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-12 text-center">
            <Users size={36} className="mx-auto text-white/30 mb-4" />
            <h3 className="font-serif-editorial text-white text-[24px]">Pas encore d’élèves rattachés</h3>
            <p className="text-white/55 text-[14px] mt-2">Communique ton code à tes élèves. Dès qu’ils le saisissent dans leur espace, ils apparaissent ici.</p>
          </div>
        ) : (
          <>
          {/* 🔎 Recherche élève (indispensable au-delà de 100 élèves) */}
          <div className="relative mb-3 w-full sm:max-w-sm" data-testid="student-search-wrap">
            <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/35 pointer-events-none" />
            <input
              value={studentSearch}
              onChange={(e) => setStudentSearch(e.target.value)}
              placeholder="Rechercher un élève (nom ou email)…"
              data-testid="student-search-input"
              className="w-full h-10 rounded-full bg-white/[0.04] border border-white/15 focus:border-emerald-500/60 outline-none pl-9 pr-9 text-white text-[13px]"
            />
            {studentSearch && (
              <button onClick={() => setStudentSearch('')} data-testid="student-search-clear" aria-label="Effacer" className="absolute right-2.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white/10 text-white/60 hover:text-white flex items-center justify-center">
                <X size={12} />
              </button>
            )}
          </div>
          {/* Filtre par formation */}
          {(() => {
            const present = [...new Set((data.students || []).map((s) => s.formula).filter(Boolean))];
            if (present.length === 0) return null;
            return (
              <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="formula-filter-bar">
                <button onClick={() => setFormulaFilter('')} data-testid="formula-filter-all" className={`px-3 h-7 rounded-full border text-[11px] font-bold ${!formulaFilter ? 'bg-white text-black border-white' : 'text-white/55 border-white/15 hover:border-white/40'}`}>Tous</button>
                {present.map((f) => {
                  const [label, cls] = FORMULA_BADGE[f] || [f, 'text-white/60 border-white/15'];
                  return (
                    <button key={f} onClick={() => setFormulaFilter(formulaFilter === f ? '' : f)} data-testid={`formula-filter-${f}`} className={`px-3 h-7 rounded-full border text-[11px] font-bold transition ${formulaFilter === f ? cls.replace('/15', '/30') + ' ring-1 ring-white/40' : cls + ' opacity-70 hover:opacity-100'}`}>{label}</button>
                  );
                })}
              </div>
            );
          })()}
          {(() => {
            const showHours = (data.students || []).some((s) => s.hours_done != null || s.hours_package != null);
            const norm = (v) => (v || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
            const q = norm(studentSearch.trim());
            const filtered = (data.students || []).filter((s) => (!formulaFilter || s.formula === formulaFilter)
              && (!q || norm(s.name).includes(q) || norm(s.email).includes(q)));
            return (
          <>
          {q && (
            <div className="text-white/45 text-[12px] mb-2" data-testid="student-search-count">{filtered.length} résultat{filtered.length > 1 ? 's' : ''} pour « {studentSearch.trim()} »</div>
          )}
          {/* ═══ Mobile : cartes élèves (pas de tableau scrollable) ═══ */}
          <div className="md:hidden space-y-3" data-testid="students-cards-mobile">
            {filtered.map((s) => {
              const pct = Math.round(((s.livret_validated ?? 0) / 38) * 100);
              return (
                <div key={s.id} className="rounded-2xl border border-white/10 bg-[#141416] p-4" style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)' }} data-testid={`student-card-${s.id}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center text-white text-[15px] font-bold shrink-0">{s.name.charAt(0).toUpperCase()}</div>
                    <div className="min-w-0 flex-1">
                      <div className="text-white text-[15px] font-semibold truncate">{s.name}</div>
                      <div className="mt-0.5"><FormulaBadge formula={s.formula} /></div>
                    </div>
                  </div>
                  <div className="mb-1 flex items-center justify-between text-[11.5px]">
                    <span className="text-zinc-400 font-mono-spec uppercase tracking-wider">Livret validé</span>
                    <span className="text-emerald-400 font-bold">{s.livret_validated ?? 0}/38 · {pct}%</span>
                  </div>
                  <div className="h-2 bg-white/8 rounded-full overflow-hidden mb-3">
                    <div className="h-full bg-gradient-to-r from-emerald-600 to-emerald-400" style={{ width: `${pct}%` }} />
                  </div>
                  {showHours && s.hours_done != null && s.hours_package ? (
                    <div className="mb-3"><HoursCell done={s.hours_done} pack={s.hours_package} /></div>
                  ) : null}
                  <Link to={`/enseignant/eleve/${s.id}`} data-testid={`student-card-detail-${s.id}`} className="flex items-center justify-center gap-2 w-full h-12 rounded-xl bg-red-600 hover:bg-red-500 active:scale-[0.98] transition text-white text-[13.5px] font-bold">
                    Voir la fiche & le livret <ArrowRight size={14} />
                  </Link>
                </div>
              );
            })}
          </div>

          {/* ═══ Desktop : tableau ═══ */}
          <div className="hidden md:block rounded-2xl border border-white/10 bg-[#0d0d0e] overflow-x-auto">
            <table className="w-full min-w-[760px] text-[13.5px]">
              <thead className="bg-white/[0.02] text-white/50 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left p-4">Élève</th>
                  {showHours && <th className="text-left p-4">Heures</th>}
                  <th className="text-left p-4 hidden md:table-cell">Livret</th>
                  <th className="text-left p-4 hidden lg:table-cell">Lecture</th>
                  <th className="text-left p-4 hidden lg:table-cell">Précision</th>
                  <th className="text-right p-4">Détail</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {(data.students || []).filter((s) => !formulaFilter || s.formula === formulaFilter).map((s) => (
                  <tr key={s.id} className="hover:bg-white/[0.02]">
                    <td className="p-3 sm:p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center text-white text-[13px] font-bold shrink-0">{s.name.charAt(0).toUpperCase()}</div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap"><span className="text-white font-semibold truncate">{s.name}</span> <FormulaBadge formula={s.formula} /></div>
                          <div className="text-white/40 text-[12px] inline-flex items-center gap-1 max-w-full"><Mail size={10} className="shrink-0" /> <span className="truncate">{s.email}</span></div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4" data-testid={`student-hours-${s.id}`} style={showHours ? undefined : { display: 'none' }}>
                      <HoursCell done={s.hours_done} pack={s.hours_package} />
                    </td>
                    <td className="p-4 hidden md:table-cell">
                      <div className="flex items-center gap-2" data-testid={`student-livret-${s.id}`}>
                        <span className="text-emerald-400 text-[14px] font-semibold">{s.livret_validated ?? 0}/38</span>
                        <div className="flex-1 max-w-[120px] h-1.5 bg-white/5 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-emerald-600 to-emerald-400" style={{ width: `${Math.round(((s.livret_validated ?? 0) / 38) * 100)}%` }} /></div>
                        <span className="text-white/50 text-[11.5px] font-mono-spec">{Math.round(((s.livret_validated ?? 0) / 38) * 100)}%</span>
                      </div>
                      <div className="text-white/35 text-[10.5px] mt-1">validées par toi · auto-éval élève : {s.livret_done}/38</div>
                    </td>
                    <td className="p-4 hidden lg:table-cell text-white/80">{s.qcm_total} articles lus</td>
                    <td className="p-4 hidden lg:table-cell"><span className={`font-serif-editorial text-[18px] ${s.qcm_accuracy >= 75 ? 'text-emerald-400' : s.qcm_accuracy >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>{s.qcm_accuracy}%</span></td>
                    <td className="p-3 sm:p-4 text-right">
                      <div className="inline-flex flex-col sm:flex-row items-end sm:items-center gap-1.5 sm:gap-2">
                        <Link to={`/enseignant/eleve/${s.id}`} data-testid={`teacher-detail-${s.id}`} className="text-red-400 hover:text-red-300 text-[13px] font-semibold">Détail →</Link>
                        <button
                          type="button"
                          onClick={async () => {
                            if (!window.confirm(`Retirer « ${s.name} » de ton roster ? L'élève garde son compte mais n'est plus rattaché à toi.`)) return;
                            try {
                              await api.teacherUnlinkStudent(s.id);
                              await fetchStudents(false);
                            } catch (err) {
                              alert(err?.response?.data?.detail || 'Erreur lors du retrait');
                            }
                          }}
                          data-testid={`teacher-unlink-${s.id}`}
                          className="text-white/40 hover:text-red-400 text-[11.5px] font-mono-spec border border-white/10 hover:border-red-500/40 rounded-full h-7 px-2.5 inline-flex items-center gap-1"
                          title="Retirer cet élève de mon roster"
                        >
                          <X size={11} /> Retirer
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
            );
          })()}
          </>
        )}
        </div>

        {/* ═══ 4. Message du jour ═══ */}
        <div className="mb-6 sm:mb-8">
          <TeacherActuPanel />
        </div>
      </section>
    </PageShell>
  );
}

export function TeacherStudentPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [exams, setExams] = useState([]);
  const [openComp, setOpenComp] = useState('C1');
  const [commentFor, setCommentFor] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [saving, setSaving] = useState(null);
  const [finalizing, setFinalizing] = useState(false);
  const [finalizedOk, setFinalizedOk] = useState(false);

  useEffect(() => {
    api.teacherStudentDetail(id).then((d) => { setData(d); setLoading(false); }).catch((e) => { setErr(e?.response?.data?.detail || 'Erreur'); setLoading(false); });
    api.listMockExams().then((r) => setExams(r?.exams || [])).catch(() => {});
  }, [id]);

  if (loading) return <PageShell><div className="max-w-7xl mx-auto p-12 text-white/50 inline-flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Chargement…</div></PageShell>;
  if (err || !data) return <PageShell><div className="max-w-3xl mx-auto p-12 text-white/50">{err}</div></PageShell>;

  const entryMap = {};
  for (const e of data.livret) entryMap[`${e.competence_code}-${e.sub_idx}`] = e;

  const setTeacherStatus = async (code, subIdx, status, comment) => {
    const key = `${code}-${subIdx}`;
    setSaving(key);
    try {
      await api.teacherValidateLivret(id, { competence_code: code, sub_idx: subIdx, teacher_status: status, teacher_comment: comment ?? (entryMap[key]?.teacher_comment || '') });
      setData((prev) => {
        const livret = [...prev.livret];
        const i = livret.findIndex((e) => e.competence_code === code && e.sub_idx === subIdx);
        const upd = { teacher_status: status, teacher_comment: comment ?? (livret[i]?.teacher_comment || '') };
        if (i >= 0) livret[i] = { ...livret[i], ...upd };
        else livret.push({ competence_code: code, sub_idx: subIdx, status: 'todo', self_eval: 0, ...upd });
        return { ...prev, livret };
      });
    } catch (_) { window.alert('Enregistrement impossible — réessaie.'); }
    setSaving(null);
  };

  const saveComment = async (code, subIdx) => {
    const key = `${code}-${subIdx}`;
    const status = entryMap[key]?.teacher_status || 'en_cours';
    await setTeacherStatus(code, subIdx, status, commentText.trim());
    setCommentFor(null);
    setCommentText('');
  };

  const totalAcquis = data.livret.filter((e) => e.teacher_status === 'acquis').length;
  const studentExams = exams.filter((x) => (x.student_name || '').trim().toLowerCase() === (data.student.name || '').trim().toLowerCase());

  const finalizeLivret = async () => {
    setFinalizing(true);
    try {
      const v = await api.teacherFinalizeLivret(id);
      setData((prev) => ({ ...prev, livret_validation: v }));
      setFinalizedOk(true); setTimeout(() => setFinalizedOk(false), 3000);
    } catch (_) { window.alert('Validation impossible — réessaie.'); }
    setFinalizing(false);
  };

  const T_STATUS = [
    { v: 'a_voir', label: 'À voir', cls: 'text-white/60 border-white/20', active: 'bg-white/15 text-white border-white/40' },
    { v: 'en_cours', label: 'En cours', cls: 'text-yellow-300/70 border-yellow-500/25', active: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50' },
    { v: 'acquis', label: 'Acquis', cls: 'text-emerald-300/70 border-emerald-500/25', active: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/50' },
  ];

  return (
    <PageShell>
      <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-10" data-testid="teacher-student-page">
        <Link to="/enseignant/dashboard" className="inline-flex items-center gap-1.5 text-white/50 hover:text-white text-[13px] mb-6"><ArrowLeft size={14} /> Tous les élèves</Link>
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center text-white text-[20px] font-bold">{data.student.name.charAt(0).toUpperCase()}</div>
            <div>
              <h1 className="font-serif-editorial text-white text-[32px] leading-tight">{data.student.name}</h1>
              <div className="text-white/50 text-[13px]">{data.student.email} · inscrit le {new Date(data.student.created_at).toLocaleDateString('fr-FR')}</div>
            </div>
          </div>
          <Link to={`/enseignant/examen-blanc-grille?eleve=${encodeURIComponent(data.student.name)}`} data-testid="teacher-new-bilan-btn" className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] font-semibold">+ Nouveau bilan</Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-white/5 rounded-2xl border border-white/10 overflow-hidden mb-8">
          <div className="bg-[#0d0d0e] p-6"><div className="text-[11px] uppercase tracking-wider text-white/40 font-semibold">Validées par toi</div><div className="font-serif-editorial text-emerald-400 text-[44px] leading-none mt-2">{totalAcquis}<span className="text-white/30 text-[20px]">/38</span></div></div>
          <div className="bg-[#0d0d0e] p-6"><div className="text-[11px] uppercase tracking-wider text-white/40 font-semibold">Auto-évaluées élève</div><div className="font-serif-editorial text-white text-[44px] leading-none mt-2">{data.livret.filter((e) => e.status === 'done').length}<span className="text-white/30 text-[20px]">/38</span></div></div>
          <div className="bg-[#0d0d0e] p-6"><div className="text-[11px] uppercase tracking-wider text-white/40 font-semibold">Bilans réalisés</div><div className="font-serif-editorial text-red-500 text-[44px] leading-none mt-2">{studentExams.length}</div></div>
        </div>

        {/* ═══ Dernières leçons + commentaires enseignants ═══ */}
        <RecentLessonsSection lessons={data.recent_lessons} />

        {/* ═══ Bilans pédagogiques (rédigés par les enseignants) ═══ */}
        <StudentBilansSection studentId={id} />

        {/* ═══ Livret interactif ═══ */}
        <h2 className="font-serif-editorial text-white text-[24px] mb-2">Livret pédagogique · validation enseignant</h2>
        <p className="text-white/45 text-[13px] mb-5">Coche l'état de chaque sous-compétence travaillée en leçon — l'élève voit ta validation dans son livret. 💬 pour ajouter un commentaire.</p>
        <div className="space-y-3 mb-12">
          {COMPETENCES.map((c) => {
            const isOpen = openComp === c.code;
            const acquis = c.subs.filter((_, i) => entryMap[`${c.code}-${i}`]?.teacher_status === 'acquis').length;
            const enCours = c.subs.filter((_, i) => entryMap[`${c.code}-${i}`]?.teacher_status === 'en_cours').length;
            return (
              <div key={c.code} className="rounded-2xl border border-white/10 bg-[#0d0d0e] overflow-hidden">
                <button onClick={() => setOpenComp(isOpen ? null : c.code)} data-testid={`teacher-livret-comp-${c.code}`} className="w-full p-5 text-left">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span className="w-9 h-9 rounded-lg bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400 font-mono-spec font-bold">{c.code}</span>
                      <div>
                        <div className="font-serif-editorial text-white text-[18px]">{c.title} <span className="text-white/40 text-[13px] font-sans">· {c.subtitle}</span></div>
                        <div className="text-white/40 text-[12px]">{c.subs.length} sous-compétences</div>
                      </div>
                    </div>
                    <span className="text-[12.5px] font-semibold shrink-0 text-right">
                      <span className="text-emerald-400">{acquis} acquises</span>
                      {enCours > 0 && <span className="text-yellow-300"> · {enCours} en cours</span>}
                      <span className="text-white/35"> / {c.subs.length}</span>
                    </span>
                  </div>
                  <div className="h-1.5 w-full bg-white/8 rounded-full overflow-hidden flex mt-3" data-testid={`comp-progress-${c.code}`}>
                    <div className="bg-emerald-500 h-full" style={{ width: `${(acquis / c.subs.length) * 100}%` }} />
                    <div className="bg-yellow-400 h-full" style={{ width: `${(enCours / c.subs.length) * 100}%` }} />
                  </div>
                </button>
                {isOpen && (
                  <div className="border-t border-white/8 divide-y divide-white/5">
                    {c.subs.map((label, i) => {
                      const e = entryMap[`${c.code}-${i}`];
                      const key = `${c.code}-${i}`;
                      const tstat = e?.teacher_status;
                      const rowCls = tstat === 'acquis' ? 'bg-emerald-500/[0.07] border-l-2 border-l-emerald-500' : tstat === 'en_cours' ? 'bg-yellow-500/[0.06] border-l-2 border-l-yellow-400' : 'border-l-2 border-l-transparent';
                      return (
                        <div key={i} className={`px-5 py-3 ${rowCls}`}>
                          <div className="flex flex-wrap items-center gap-3">
                            <span className="text-white/40 font-mono-spec text-[11.5px] w-12 shrink-0">{c.code}.{(i + 1).toString().padStart(2, '0')}</span>
                            <span className="flex-1 min-w-[180px] text-white/80 text-[13px]">{label}</span>
                            {/* auto-éval élève */}
                            <span className="flex items-center gap-1" title="Auto-évaluation de l'élève">
                              {[1, 2, 3, 4, 5].map((n) => <Star key={n} size={10} className={n <= (e?.self_eval || 0) ? 'text-red-400 fill-red-400' : 'text-white/12'} />)}
                            </span>
                            {/* validation enseignant */}
                            <span className="flex items-center gap-1.5">
                              {T_STATUS.map((s) => {
                                const active = e?.teacher_status === s.v;
                                return (
                                  <button
                                    key={s.v}
                                    onClick={() => setTeacherStatus(c.code, i, s.v)}
                                    disabled={saving === key}
                                    data-testid={`tl-${c.code}-${i}-${s.v}`}
                                    className={`h-7 px-2.5 rounded-full border text-[10.5px] font-bold uppercase tracking-wide transition ${active ? s.active : `${s.cls} hover:border-white/40`} disabled:opacity-40`}
                                  >{s.label}</button>
                                );
                              })}
                              <button
                                onClick={() => { setCommentFor(commentFor === key ? null : key); setCommentText(e?.teacher_comment || ''); }}
                                data-testid={`tl-${c.code}-${i}-comment`}
                                className={`h-7 w-7 rounded-full border text-[12px] ${e?.teacher_comment ? 'border-sky-500/50 text-sky-300' : 'border-white/15 text-white/40'} hover:border-white/40`}
                                title={e?.teacher_comment || 'Ajouter un commentaire'}
                              >💬</button>
                            </span>
                          </div>
                          {commentFor === key && (
                            <div className="mt-2.5 flex gap-2">
                              <input value={commentText} onChange={(ev) => setCommentText(ev.target.value)} maxLength={600} placeholder="Commentaire visible par l'élève…" className="flex-1 h-9 px-3 rounded-lg bg-black/40 border border-white/15 text-white text-[12.5px] outline-none focus:border-red-500" data-testid={`tl-${c.code}-${i}-comment-input`} />
                              <button onClick={() => saveComment(c.code, i)} className="h-9 px-4 rounded-lg bg-red-600 hover:bg-red-500 text-white text-[12px] font-semibold" data-testid={`tl-${c.code}-${i}-comment-save`}>OK</button>
                            </div>
                          )}
                          {e?.teacher_comment && commentFor !== key && (
                            <div className="mt-1.5 text-sky-300/70 text-[12px] pl-14">💬 {e.teacher_comment}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ═══ Bouton de validation globale du livret ═══ */}
        <div className="rounded-2xl border border-emerald-700/30 bg-emerald-950/10 p-4 sm:p-5 mb-12 flex flex-wrap items-center gap-3" data-testid="livret-finalize-bar">
          <div className="flex-1 min-w-[220px]">
            <div className="text-white text-[14px] font-semibold">Finaliser la validation du livret</div>
            <div className="text-white/50 text-[12px] mt-0.5">
              {data.livret_validation
                ? <>Dernière validation : <b className="text-emerald-300">{new Date(data.livret_validation.validated_at).toLocaleDateString('fr-FR')} à {new Date(data.livret_validation.validated_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</b> par {data.livret_validation.teacher_name} · {data.livret_validation.count_acquis} compétences acquises</>
                : 'Chaque coche est enregistrée automatiquement — ce bouton horodate ta validation officielle, visible par l\'élève.'}
            </div>
          </div>
          <button
            type="button"
            onClick={finalizeLivret}
            disabled={finalizing}
            data-testid="livret-finalize-btn"
            className={`h-11 px-6 rounded-full text-[13.5px] font-bold inline-flex items-center gap-2 transition ${finalizedOk ? 'bg-emerald-500 text-emerald-950' : 'bg-emerald-600 hover:bg-emerald-500 text-white'} disabled:opacity-50`}
          >
            {finalizing ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
            {finalizedOk ? 'Livret validé !' : `Valider le livret (${totalAcquis}/38 acquises)`}
          </button>
        </div>

        {/* ═══ Bilans de formation ═══ */}
        <h2 className="font-serif-editorial text-white text-[24px] mb-2">Bilans de formation</h2>
        <p className="text-white/45 text-[13px] mb-5">Tous les bilans et examens blancs réalisés avec {data.student.name.split(' ')[0]}.</p>
        {studentExams.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-8 text-center mb-10" data-testid="teacher-student-no-bilan">
            <div className="text-white/60 text-[14px] font-semibold">Aucun bilan pour cet élève</div>
            <Link to={`/enseignant/examen-blanc-grille?eleve=${encodeURIComponent(data.student.name)}`} className="inline-flex items-center gap-2 mt-4 h-10 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] font-semibold">Créer le premier bilan →</Link>
          </div>
        ) : (
          <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] overflow-hidden mb-10" data-testid="teacher-student-bilans">
            <table className="w-full text-[13px]">
              <thead><tr className="text-white/40 text-[11px] uppercase tracking-wider border-b border-white/8">
                <th className="text-left p-4">Date</th><th className="text-left p-4 hidden sm:table-cell">Lieu</th><th className="text-left p-4">Verdict</th><th className="text-left p-4 hidden sm:table-cell">Réussite</th><th className="p-4"></th>
              </tr></thead>
              <tbody className="divide-y divide-white/5">
                {studentExams.map((x) => (
                  <tr key={x.id}>
                    <td className="p-4 text-white/80 font-mono-spec text-[12.5px]">{x.date}</td>
                    <td className="p-4 text-white/55 hidden sm:table-cell">{x.lieu || '—'}</td>
                    <td className="p-4"><span className={`text-[12px] font-bold ${x.verdict_kind === 'favorable' ? 'text-emerald-400' : x.verdict_kind === 'defavorable' ? 'text-red-400' : 'text-yellow-300'}`}>{x.verdict || '—'}</span></td>
                    <td className="p-4 text-white/70 hidden sm:table-cell">{typeof x.ok_pct === 'number' ? `${x.ok_pct}%` : '—'}</td>
                    <td className="p-4 text-right"><a href={`/examen-blanc/${x.token}`} target="_blank" rel="noreferrer" className="text-red-400 hover:text-red-300 text-[12.5px] font-semibold" data-testid={`open-bilan-${x.id}`}>Ouvrir →</a></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </PageShell>
  );
}

export function StudentLinkTeacherWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  if (!user || user.role !== 'eleve') return null;

  const link = async () => {
    setErr(''); setMsg(''); setLoading(true);
    try {
      const r = await api.linkTeacher(code.toUpperCase().trim());
      setMsg(`Rattaché à ${r.teacher_name}`); setCode('');
      // Re-fetch user via context refresh
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };
  const unlink = async () => {
    setLoading(true);
    await api.unlinkTeacher();
    setLoading(false);
    window.location.reload();
  };

  if (user.linked_teacher_id) {
    return (
      <div className="rounded-2xl border border-emerald-700/30 bg-emerald-950/10 p-5 flex items-center justify-between gap-4 flex-wrap mb-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-emerald-600/20 border border-emerald-600/30 flex items-center justify-center text-emerald-400"><CheckCircle2 size={16} /></div>
          <div>
            <div className="text-white text-[14px] font-semibold">Tu es rattaché à un enseignant ECSR</div>
            <div className="text-white/55 text-[12.5px]">Ton enseignant voit ta progression en temps réel.</div>
          </div>
        </div>
        <button onClick={unlink} className="h-9 px-3 rounded-full border border-white/15 text-white/70 hover:border-white/30 text-[12.5px]">Détacher</button>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 mb-6">
      {!open ? (
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400"><LinkIcon size={16} /></div>
            <div>
              <div className="text-white text-[14px] font-semibold">Rattache-toi à ton moniteur ECSR</div>
              <div className="text-white/55 text-[12.5px]">Saisis le code communiqué par ton auto-école pour synchroniser ta progression.</div>
            </div>
          </div>
          <button onClick={() => setOpen(true)} className="h-9 px-4 rounded-full bg-red-600 hover:bg-red-500 text-white text-[12.5px] font-semibold">Saisir un code</button>
        </div>
      ) : (
        <div>
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="text-white text-[14px] font-semibold">Code enseignant (6 caractères)</div>
            <button onClick={() => setOpen(false)} className="text-white/40 hover:text-white"><X size={16} /></button>
          </div>
          <div className="flex gap-2">
            <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={6} placeholder="ABC123" className="flex-1 h-11 px-4 rounded-lg bg-white/5 border border-white/15 text-white text-[16px] outline-none focus:border-red-500 font-mono-spec tracking-widest text-center" />
            <button onClick={link} disabled={loading || code.length < 4} className="h-11 px-5 rounded-lg bg-red-600 hover:bg-red-500 text-white text-[13.5px] font-semibold disabled:opacity-50">{loading ? '...' : 'Rattacher'}</button>
          </div>
          {msg && <div className="mt-2 text-emerald-400 text-[12.5px]">{msg}</div>}
          {err && <div className="mt-2 text-red-400 text-[12.5px]">{err}</div>}
        </div>
      )}
    </div>
  );
}
